import { UseTranslationOptions } from "react-i18next";
import { translationHook } from "./internal/translationHook";
import {
  LxI18NResource,
  LxTranslationHook,
  TranslationKeys,
} from "./LxI18N.model";

/**
 * A hook that provides a type-save translation (`t`) function.
 *
 * Use it in conjunction with the `makeLxTranslations` function,
 * the `lxo-i18n-extract` extraction tool as well as the `LxI18N` provider.
 *
 * Note: you need a `React.Suspense` component in your app
 * to use this hook as the translation resources are
 * loaded asynchronously.
 *
 * @package I18N
 * @example
 * const MY_TRANSLATIONS = makeLxTranslations({
 *  translations: {
 *   "hello": "Hallo",
 *   "world": "Welt"
 *  }
 * })
 *
 * function MyComponent() {
 *  const { t } = useLxTranslations(MY_TRANSLATIONS);
 *
 *  return ( <div>{t("hello")} {t("world")}</div> );
 * }
 *
 */
export function useLxTranslations<T extends LxI18NResource>(
  resource: T,
  options?: UseTranslationOptions
): LxTranslationHook<TranslationKeys<T>> {
  const hook = translationHook([resource]);
  return hook(options);
}
